/**
 * PipelineForm — Create or edit a pipeline, source/target connection config,
 * credentials, and connection test, all on a single screen.
 *
 * Streamlined to match how every other agent's "Connect Database" flow
 * works in this app (ConnectDatabaseDialog + the shared connector registry
 * at `ztd_sdk.connectors`, exposed via `apiv2.listSupportedConnectors`):
 * one registry-driven set of Source/Target Type options (instead of a
 * hand-typed list that can drift out of sync — the root cause of two real
 * bugs elsewhere: Data Converter's dropdown missing Snowflake, the
 * Profiler's dropdown advertising unsupported MariaDB/Redshift), plus
 * registry-driven extra fields (fixes a real gap here: Snowflake had no
 * account/warehouse/role fields at all; Oracle had no service_name field).
 *
 * Credentials are merged directly into this form (previously a separate
 * CredentialsPanel navigated to after create). NFR9 security invariants are
 * unchanged: password/secret inputs are type="password", ALWAYS start
 * empty, are read from refs (never React state), and vault refs are shown
 * as opaque read-only text — never as input values.
 *
 * Calls, in order, on Save:
 *   POST /api/v1/pipeline-engineer/pipelines/            (create) or
 *   PUT  /api/v1/pipeline-engineer/pipelines/{id}         (update)
 *   POST /api/v1/pipeline-engineer/pipelines/{id}/credentials  (FR91, if any provided)
 *   POST /api/v1/pipeline-engineer/pipelines/{id}/test-connection (FR92, FR4, if credentials were just set)
 *
 * NOTE: pipeline_engineer's own runtime connector dispatch
 * (agents/pipeline_engineer/engine/connectors/registry.py) uses the key
 * "postgres", while the shared registry's canonical value is "postgresql"
 * (with "postgres" as an alias) — toPeDbType() below maps registry values to
 * PE's own values so a mismatch can't silently break job execution.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Check, Loader2 } from "lucide-react";
import {
  apiv2,
  type ConnectorFieldOut,
  type ConnectorOut,
  type DBConfigOut,
} from "../../apiv2";
import type {
  PEPipeline,
  PipelineCreatePayload,
  PipelineUpdatePayload,
  CredentialsResult,
} from "../../features/pipeline-engineer/types";
import {
  createPipeline,
  deletePipeline,
  updatePipeline,
  storeCredentials,
  testConnection,
  testConnectionAdhoc,
  connectExistingConnection,
} from "../../features/pipeline-engineer/api/pe-client";
import { SchemaAccessPanel } from "./SchemaAccessPanel";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Fallback only — used before the registry fetch resolves, or if it fails.
 * Every real render should end up using the fetched, registry-driven list
 * instead of this (see the module docstring for why). */
const DEFAULT_CONNECTOR_OPTIONS = [
  { value: "mysql", label: "MySQL" },
  { value: "postgres", label: "PostgreSQL" },
  { value: "oracle", label: "Oracle" },
  { value: "mssql", label: "SQL Server" },
  { value: "flatfile", label: "Flat File" },
  { value: "redshift", label: "Redshift" },
  { value: "snowflake", label: "Snowflake" },
  { value: "databricks", label: "Databricks" },
] as const;

/** Not offered by the shared connector registry at all (pipeline_engineer
 * has its own flat-file connector with no host/port/credentials). */
const FLATFILE_OPTION = { value: "flatfile", label: "Flat File" };

/** Shared registry canonical value → pipeline_engineer's own dispatch key
 * (agents/pipeline_engineer/engine/connectors/registry.py). Only postgres
 * differs; every other type pipeline_engineer supports matches 1:1. */
function toPeDbType(registryDbType: string): string {
  return registryDbType === "postgresql" ? "postgres" : registryDbType;
}

/** Connectors that use OAuth M2M — form shows http_path; credentials use client_id/secret. */
const OAUTH_TARGET_TYPES = new Set(["databricks"]);

/** Extra fields the registry lists that are actually credential material
 * (handled by the Credentials sections below, via storeCredentials) rather
 * than pipeline config — must not be double-rendered as generic config
 * inputs feeding into source_config/target_config. */
function isConfigExtraField(field: ConnectorFieldOut): boolean {
  return (
    !field.secret && field.key !== "client_id" && field.key !== "client_secret"
  );
}

function targetConfigLabel(targetType: string): {
  host: string;
  catalog: string;
  schema: string;
} {
  if (targetType === "databricks") {
    return {
      host: "Workspace Host",
      catalog: "Catalog",
      schema: "Default Schema",
    };
  }
  return { host: "Host", catalog: "Database", schema: "Schema" };
}

function sameConnectionConfig(
  left: Record<string, string | null | undefined>,
  right: Record<string, string | null | undefined>,
): boolean {
  const normalize = (config: Record<string, string | null | undefined>) =>
    Object.entries(config)
      .filter(([, value]) => value != null && value.trim() !== "")
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));
  return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
}

// ---------------------------------------------------------------------------
// Helpers (styling)
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const LABEL_CLASS = "block text-xs font-medium text-white/60 mb-1";

const SECTION_CLASS =
  "text-sm font-semibold text-white/80 border-b border-white/10 pb-2 mb-3";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PipelineFormProps {
  /** Scopes the saved-connection picker ("reuse a saved connection") to this
   * project's library, and is sent as `project_id` on create. */
  projectId: string;
  /** Provide for edit mode; omit for create mode. */
  pipeline?: PEPipeline;
  onSaved: (pipeline: PEPipeline) => void;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PipelineForm({
  projectId,
  pipeline,
  onSaved,
  onCancel,
}: PipelineFormProps) {
  const isEdit = pipeline !== undefined;

  // ── Registry-driven connector options ────────────────────────────────────
  const [connectors, setConnectors] = useState<ConnectorOut[]>([]);

  useEffect(() => {
    let cancelled = false;
    apiv2
      .listSupportedConnectors("pipeline_engineer")
      .then((result) => {
        if (!cancelled) setConnectors(result);
      })
      .catch(() => {
        if (!cancelled) setConnectors([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const connectorOptions =
    connectors.length > 0
      ? [
          ...connectors.map((c) => ({
            value: toPeDbType(c.db_type),
            label: c.label,
          })),
          FLATFILE_OPTION,
        ]
      : DEFAULT_CONNECTOR_OPTIONS;

  // ── Pipeline row state (this pipeline, once created/known) ──────────────
  const [savedPipeline, setSavedPipeline] = useState<PEPipeline | undefined>(
    pipeline,
  );

  // Form field state
  const [pipelineName, setPipelineName] = useState(
    pipeline?.pipelineName ?? "",
  );
  const [description, setDescription] = useState(pipeline?.description ?? "");
  const [sourceType, setSourceType] = useState(pipeline?.sourceType ?? "mysql");
  const [sourceHost, setSourceHost] = useState(
    pipeline?.sourceConfig.host ?? "",
  );
  const [sourcePort, setSourcePort] = useState(
    pipeline?.sourceConfig.port ?? "",
  );
  const [sourceDatabase, setSourceDatabase] = useState(
    pipeline?.sourceConfig.database ?? "",
  );
  const [sourceSchema, setSourceSchema] = useState(
    pipeline?.sourceConfig.schema ?? "",
  );
  const [sourceExtra, setSourceExtra] = useState<Record<string, string>>(() =>
    stripCoreKeys(pipeline?.sourceConfig),
  );
  const [targetType, setTargetType] = useState(
    pipeline?.targetType ?? "databricks",
  );
  const [targetHost, setTargetHost] = useState(
    pipeline?.targetConfig.host ?? "",
  );
  const [targetPort, setTargetPort] = useState(
    pipeline?.targetConfig.port ?? "",
  );
  const [targetHttpPath, setTargetHttpPath] = useState(
    pipeline?.targetConfig.http_path ?? "",
  );
  const [targetCatalog, setTargetCatalog] = useState(
    pipeline?.targetConfig.catalog ?? "",
  );
  const [targetSchema, setTargetSchema] = useState(
    pipeline?.targetConfig.schema ?? "",
  );
  const [targetExtra, setTargetExtra] = useState<Record<string, string>>(() =>
    stripCoreKeys(pipeline?.targetConfig, ["workspace_host", "http_path"]),
  );

  // Credentials state (refs — plaintext never lives in React state)
  const sourceUsernameRef = useRef<HTMLInputElement>(null);
  const sourcePasswordRef = useRef<HTMLInputElement>(null);
  const targetClientIdRef = useRef<HTMLInputElement>(null);
  const targetClientSecretRef = useRef<HTMLInputElement>(null);
  const targetUsernameRef = useRef<HTMLInputElement>(null);
  const targetPasswordRef = useRef<HTMLInputElement>(null);

  const [savedRefs, setSavedRefs] = useState<CredentialsResult>({
    sourceSecretRef: pipeline?.sourceSecretRef,
    targetSecretRef: pipeline?.targetSecretRef,
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sourceTesting, setSourceTesting] = useState(false);
  const [sourceTestResult, setSourceTestResult] = useState<{
    ok: boolean;
    error?: string;
  } | null>(null);
  const [sourceTestError, setSourceTestError] = useState<string | null>(null);

  const [targetTesting, setTargetTesting] = useState(false);
  const [targetTestResult, setTargetTestResult] = useState<{
    ok: boolean;
    error?: string;
  } | null>(null);
  const [targetTestError, setTargetTestError] = useState<string | null>(null);

  // ── Reuse a saved connection (same cross-agent library Data Converter's
  // SavedConnectionsPanel and every ConnectDatabaseDialog agent already
  // read from) ──────────────────────────────────────────────────────────
  const [sourceConnections, setSourceConnections] = useState<DBConfigOut[]>([]);
  const [targetConnections, setTargetConnections] = useState<DBConfigOut[]>([]);
  const [selectedSourceConnId, setSelectedSourceConnId] = useState("");
  const [selectedTargetConnId, setSelectedTargetConnId] = useState("");
  const [applyingSourceConn, setApplyingSourceConn] = useState(false);
  const [applyingTargetConn, setApplyingTargetConn] = useState(false);
  const [reuseError, setReuseError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiv2
      .listSourceConnections(projectId)
      .then((conns) => {
        if (!cancelled) setSourceConnections(conns);
      })
      .catch(() => {
        if (!cancelled) setSourceConnections([]);
      });
    apiv2
      .listTargetConnections(projectId)
      .then((conns) => {
        if (!cancelled) setTargetConnections(conns);
      })
      .catch(() => {
        if (!cancelled) setTargetConnections([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  function connLabel(c: DBConfigOut): string {
    const detail = [c.host, c.database].filter(Boolean).join("/");
    return c.name
      ? `${c.name} (${c.db_type}${detail ? ` · ${detail}` : ""})`
      : `${c.db_type}${detail ? ` · ${detail}` : ""}`;
  }

  /** Populates all form fields from a pipeline just returned by
   * connect-existing (or create/update) so the UI reflects the applied
   * connection without requiring a page refresh. */
  function applyPipelineToFields(p: PEPipeline) {
    setSourceType(p.sourceType);
    setSourceHost(p.sourceConfig.host ?? "");
    setSourcePort(p.sourceConfig.port ?? "");
    setSourceDatabase(p.sourceConfig.database ?? "");
    setSourceSchema(p.sourceConfig.schema ?? "");
    setSourceExtra(stripCoreKeys(p.sourceConfig));
    setTargetType(p.targetType);
    setTargetHost(p.targetConfig.host ?? "");
    setTargetPort(p.targetConfig.port ?? "");
    setTargetHttpPath(p.targetConfig.http_path ?? "");
    setTargetCatalog(p.targetConfig.database ?? p.targetConfig.catalog ?? "");
    setTargetSchema(p.targetConfig.schema ?? "");
    setTargetExtra(
      stripCoreKeys(p.targetConfig, ["workspace_host", "http_path"]),
    );
  }

  /** Same shape handleSubmit sends — shared so an auto-create-on-first-reuse
   * (below) and the real Save button never drift apart. */
  function buildSourceConfig(): Record<string, string> {
    return {
      host: sourceHost,
      port: sourcePort,
      database: sourceDatabase,
      schema: sourceSchema,
      ...sourceExtra,
    };
  }

  function buildTargetConfig(): Record<string, string> {
    return isOAuthTarget
      ? {
          host: targetHost,
          workspace_host: targetHost,
          http_path: targetHttpPath,
          catalog: targetCatalog,
          schema: targetSchema,
          ...targetExtra,
        }
      : {
          host: targetHost,
          port: targetPort,
          database: targetCatalog,
          schema: targetSchema,
          ...targetExtra,
        };
  }

  /** Applies a saved connection to the form — auto-triggered the moment the
   * "Reuse a saved connection" dropdown changes (no separate click needed;
   * `connectionIdOverride` sidesteps the state-update lag so the dropdown's
   * own onChange can pass the just-selected id instead of the stale state
   * value), and still callable from the "Use This Connection" button for a
   * manual retry after a failure. */
  const handleUseSavedConnection = async (
    side: "source" | "target",
    connectionIdOverride?: string,
  ) => {
    const connectionId =
      connectionIdOverride ??
      (side === "source" ? selectedSourceConnId : selectedTargetConnId);
    if (!connectionId) return;
    setReuseError(null);
    const setApplying =
      side === "source" ? setApplyingSourceConn : setApplyingTargetConn;
    setApplying(true);
    let newlyCreatedPipelineId: string | undefined;
    try {
      // Create mode with no pipeline row yet — reusing a connection is the
      // first real action, same as every other agent's "Connect Database"
      // dialog (connecting IS what creates the underlying session/entity).
      let target = savedPipeline;
      if (!target) {
        if (!pipelineName.trim()) {
          setReuseError(
            "Enter a pipeline name above first, then reuse a saved connection.",
          );
          return;
        }
        target = await createPipeline({
          pipeline_name: pipelineName.trim(),
          description: description.trim() || undefined,
          source_type: sourceType,
          source_config: buildSourceConfig(),
          target_type: targetType,
          target_config: buildTargetConfig(),
          project_id: projectId,
        });
        newlyCreatedPipelineId = target.id;
        setSavedPipeline(target);
      }
      const updated = await connectExistingConnection(
        target.id,
        side,
        connectionId,
      );
      setSavedPipeline(updated);
      setSavedRefs({
        sourceSecretRef: updated.sourceSecretRef,
        targetSecretRef: updated.targetSecretRef,
      });
      applyPipelineToFields(updated);
    } catch (err) {
      let message =
        err instanceof Error ? err.message : "Failed to reuse connection.";
      if (newlyCreatedPipelineId) {
        try {
          await deletePipeline(newlyCreatedPipelineId);
          setSavedPipeline(undefined);
        } catch (cleanupError) {
          const cleanupMessage =
            cleanupError instanceof Error
              ? cleanupError.message
              : "Pipeline cleanup failed.";
          message = `${message} The new pipeline could not be removed: ${cleanupMessage}`;
        }
      }
      setReuseError(message);
    } finally {
      setApplying(false);
    }
  };

  const targetLabels = targetConfigLabel(targetType);
  const isOAuthTarget = OAUTH_TARGET_TYPES.has(targetType);

  const activeSourceConnector = connectors.find(
    (c) => toPeDbType(c.db_type) === sourceType,
  );
  const activeTargetConnector = connectors.find(
    (c) => toPeDbType(c.db_type) === targetType,
  );
  const sourceExtraFields = (activeSourceConnector?.extra_fields ?? []).filter(
    isConfigExtraField,
  );
  // OAuth targets already render their own "SQL Warehouse Path" input bound to
  // http_path; the registry's identical field must not be rendered (and its
  // `required` enforced, and its value spread over buildTargetConfig) a second
  // time, or filling the real input still fails "HTTP Path is required".
  const targetExtraFields = (activeTargetConnector?.extra_fields ?? []).filter(
    (field) =>
      isConfigExtraField(field) &&
      !(isOAuthTarget && field.key === "http_path"),
  );

  // Test source connection only — independent of target (FR4). Prefers
  // whatever is currently typed into the form (works before the pipeline is
  // ever saved, via the stateless ad-hoc endpoint); only falls back to
  // testing the already-stored credentials when nothing new was typed AND
  // the pipeline already has stored source credentials.
  const handleTestSource = async () => {
    setSourceTesting(true);
    setSourceTestError(null);
    setSourceTestResult(null);
    try {
      const typedUsername = sourceUsernameRef.current?.value.trim() ?? "";
      const typedPassword = sourcePasswordRef.current?.value ?? "";
      const hasTypedCreds = Boolean(typedUsername) || Boolean(typedPassword);

      const savedSourceConfigUnchanged =
        savedPipeline?.sourceType === sourceType &&
        sameConnectionConfig(savedPipeline.sourceConfig, buildSourceConfig());
      if (!hasTypedCreds && savedPipeline && savedRefs.sourceSecretRef) {
        if (!savedSourceConfigUnchanged) {
          setSourceTestError(
            "Save the changed source settings before testing with saved credentials, or enter credentials for an ad-hoc test.",
          );
          return;
        }
        const result = await testConnection(savedPipeline.id, "source");
        setSourceTestResult({
          ok: result.sourceOk ?? false,
          error: result.sourceError,
        });
        return;
      }

      const result = await testConnectionAdhoc({
        side: "source",
        dbType: sourceType,
        config: buildSourceConfig(),
        username: typedUsername,
        password: typedPassword,
      });
      setSourceTestResult({
        ok: result.sourceOk ?? false,
        error: result.sourceError,
      });
    } catch (err) {
      setSourceTestError(
        err instanceof Error ? err.message : "Test connection failed.",
      );
    } finally {
      setSourceTesting(false);
    }
  };

  // Test target connection only — independent of source (FR4). Same
  // typed-creds-first / stored-creds-fallback pattern as handleTestSource.
  const handleTestTarget = async () => {
    setTargetTesting(true);
    setTargetTestError(null);
    setTargetTestResult(null);
    try {
      const typedClientId = targetClientIdRef.current?.value.trim() ?? "";
      const typedClientSecret = targetClientSecretRef.current?.value ?? "";
      const typedUsername = targetUsernameRef.current?.value.trim() ?? "";
      const typedPassword = targetPasswordRef.current?.value ?? "";
      const hasTypedCreds = isOAuthTarget
        ? Boolean(typedClientId) || Boolean(typedClientSecret)
        : Boolean(typedUsername) || Boolean(typedPassword);

      const savedTargetConfigUnchanged =
        savedPipeline?.targetType === targetType &&
        sameConnectionConfig(savedPipeline.targetConfig, buildTargetConfig());
      if (!hasTypedCreds && savedPipeline && savedRefs.targetSecretRef) {
        if (!savedTargetConfigUnchanged) {
          setTargetTestError(
            "Save the changed target settings before testing with saved credentials, or enter credentials for an ad-hoc test.",
          );
          return;
        }
        const result = await testConnection(savedPipeline.id, "target");
        setTargetTestResult({
          ok: result.targetOk ?? false,
          error: result.targetError,
        });
        return;
      }

      const result = await testConnectionAdhoc(
        isOAuthTarget
          ? {
              side: "target",
              dbType: targetType,
              config: buildTargetConfig(),
              clientId: typedClientId,
              clientSecret: typedClientSecret,
            }
          : {
              side: "target",
              dbType: targetType,
              config: buildTargetConfig(),
              username: typedUsername,
              password: typedPassword,
            },
      );
      setTargetTestResult({
        ok: result.targetOk ?? false,
        error: result.targetError,
      });
    } catch (err) {
      setTargetTestError(
        err instanceof Error ? err.message : "Test connection failed.",
      );
    } finally {
      setTargetTesting(false);
    }
  };

  // Save (config + credentials, in one action)
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!pipelineName.trim()) {
      setError("Pipeline name is required.");
      return;
    }
    // The form renders with noValidate, so the browser's own `required`
    // enforcement never fires here — check it ourselves (FR: prevent
    // saving a pipeline with a connector-required field left blank, e.g.
    // Oracle's service_name, which the connector silently ignores as
    // absent rather than erroring at save time).
    const missingSourceField = sourceExtraFields.find(
      (field) => field.required && !sourceExtra[field.key]?.trim(),
    );
    if (missingSourceField) {
      setError(`Source: ${missingSourceField.label} is required.`);
      return;
    }
    const missingTargetField = targetExtraFields.find(
      (field) => field.required && !targetExtra[field.key]?.trim(),
    );
    if (missingTargetField) {
      setError(`Target: ${missingTargetField.label} is required.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    setSourceTestError(null);
    setTargetTestError(null);
    try {
      const sourceConfig = buildSourceConfig();
      const targetConfig = buildTargetConfig();

      let saved: PEPipeline;
      if (savedPipeline) {
        const body: PipelineUpdatePayload = {
          pipeline_name: pipelineName.trim(),
          description: description.trim() || undefined,
          source_type: sourceType,
          source_config: sourceConfig,
          target_type: targetType,
          target_config: targetConfig,
        };
        saved = await updatePipeline(savedPipeline.id, body);
      } else {
        const body: PipelineCreatePayload = {
          pipeline_name: pipelineName.trim(),
          description: description.trim() || undefined,
          source_type: sourceType,
          source_config: sourceConfig,
          target_type: targetType,
          target_config: targetConfig,
          project_id: projectId,
        };
        saved = await createPipeline(body);
      }
      setSavedPipeline(saved);

      // Store credentials, only if the user actually entered any this time.
      const sourceCredsEntered =
        Boolean(sourceUsernameRef.current?.value.trim()) ||
        Boolean(sourcePasswordRef.current?.value);
      const targetCredsEntered = isOAuthTarget
        ? Boolean(targetClientIdRef.current?.value.trim()) ||
          Boolean(targetClientSecretRef.current?.value)
        : Boolean(targetUsernameRef.current?.value.trim()) ||
          Boolean(targetPasswordRef.current?.value);

      const credsPayload = isOAuthTarget
        ? {
            source_username:
              sourceUsernameRef.current?.value.trim() || undefined,
            source_password: sourcePasswordRef.current?.value || undefined,
            target_client_id:
              targetClientIdRef.current?.value.trim() || undefined,
            target_client_secret:
              targetClientSecretRef.current?.value || undefined,
          }
        : {
            source_username:
              sourceUsernameRef.current?.value.trim() || undefined,
            source_password: sourcePasswordRef.current?.value || undefined,
            target_username:
              targetUsernameRef.current?.value.trim() || undefined,
            target_password: targetPasswordRef.current?.value || undefined,
          };
      const hasAnyCred = sourceCredsEntered || targetCredsEntered;

      if (hasAnyCred) {
        const refs = await storeCredentials(saved.id, credsPayload);
        setSavedRefs(refs);
        // Clear password inputs after successful save (security hygiene).
        if (sourcePasswordRef.current) sourcePasswordRef.current.value = "";
        if (targetClientSecretRef.current)
          targetClientSecretRef.current.value = "";
        if (targetPasswordRef.current) targetPasswordRef.current.value = "";

        // New credentials were just set — verify them immediately, per side,
        // so a typo is caught here instead of at job-run time. Only the
        // side(s) that just got new credentials are tested (FR4 —
        // independent per-side testing, matching the two separate "Test
        // Connection" buttons). Stay on the form if either fails so the
        // user can fix and retry without losing their place.
        let anySideFailed = false;

        if (sourceCredsEntered) {
          setSourceTesting(true);
          try {
            const result = await testConnection(saved.id, "source");
            setSourceTestResult({
              ok: result.sourceOk ?? false,
              error: result.sourceError,
            });
            if (!result.sourceOk) anySideFailed = true;
          } catch (err) {
            setSourceTestError(
              err instanceof Error ? err.message : "Test connection failed.",
            );
            anySideFailed = true;
          } finally {
            setSourceTesting(false);
          }
        }

        if (targetCredsEntered) {
          setTargetTesting(true);
          try {
            const result = await testConnection(saved.id, "target");
            setTargetTestResult({
              ok: result.targetOk ?? false,
              error: result.targetError,
            });
            if (!result.targetOk) anySideFailed = true;
          } catch (err) {
            setTargetTestError(
              err instanceof Error ? err.message : "Test connection failed.",
            );
            anySideFailed = true;
          } finally {
            setTargetTesting(false);
          }
        }

        if (anySideFailed) {
          return;
        }
      }

      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save pipeline.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl p-6 space-y-5">
      <h2 className="text-base font-semibold text-white">
        {isEdit ? "Edit Pipeline" : "Create Pipeline"}
      </h2>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="space-y-5"
        noValidate
      >
        {/* ── Core fields ──────────────────────────────────────────────── */}
        <div className="space-y-4">
          <div>
            <label htmlFor="pe-pipeline-name" className={LABEL_CLASS}>
              Pipeline Name{" "}
              <span className="text-red-400" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="pe-pipeline-name"
              type="text"
              required
              value={pipelineName}
              onChange={(e) => setPipelineName(e.target.value)}
              placeholder="e.g. mysql-to-databricks-orders"
              className={INPUT_CLASS}
              aria-required="true"
            />
          </div>

          <div>
            <label htmlFor="pe-description" className={LABEL_CLASS}>
              Description
            </label>
            <textarea
              id="pe-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>
        </div>

        {/* ── Source connection ─────────────────────────────────────────── */}
        <div>
          <p className={SECTION_CLASS}>Source Connection</p>
          <div className="space-y-3">
            {sourceConnections.length > 0 && (
              <div className="flex items-end gap-2 pb-1">
                <div className="flex-1">
                  <label htmlFor="pe-source-saved-conn" className={LABEL_CLASS}>
                    Reuse a saved connection
                  </label>
                  <select
                    id="pe-source-saved-conn"
                    value={selectedSourceConnId}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedSourceConnId(value);
                      if (value) void handleUseSavedConnection("source", value);
                    }}
                    className={INPUT_CLASS}
                  >
                    <option value="">Choose a saved connection…</option>
                    {sourceConnections.map((c) => (
                      <option key={c.id} value={c.id}>
                        {connLabel(c)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => void handleUseSavedConnection("source")}
                  disabled={!selectedSourceConnId || applyingSourceConn}
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {applyingSourceConn && (
                    <Loader2
                      size={12}
                      className="animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  Use This Connection
                </button>
              </div>
            )}
            <div>
              <label htmlFor="pe-source-type" className={LABEL_CLASS}>
                Source Type
              </label>
              <select
                id="pe-source-type"
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                className={INPUT_CLASS}
              >
                {connectorOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="pe-source-host" className={LABEL_CLASS}>
                  Host
                </label>
                <input
                  id="pe-source-host"
                  type="text"
                  value={sourceHost}
                  onChange={(e) => setSourceHost(e.target.value)}
                  placeholder="db.example.com"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="pe-source-port" className={LABEL_CLASS}>
                  Port
                </label>
                <input
                  id="pe-source-port"
                  type="text"
                  inputMode="numeric"
                  value={sourcePort}
                  onChange={(e) => setSourcePort(e.target.value)}
                  placeholder={
                    activeSourceConnector?.default_port
                      ? String(activeSourceConnector.default_port)
                      : "3306"
                  }
                  className={INPUT_CLASS}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="pe-source-database" className={LABEL_CLASS}>
                  Database
                </label>
                <input
                  id="pe-source-database"
                  type="text"
                  value={sourceDatabase}
                  onChange={(e) => setSourceDatabase(e.target.value)}
                  placeholder="my_database"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="pe-source-schema" className={LABEL_CLASS}>
                  Schema
                </label>
                <input
                  id="pe-source-schema"
                  type="text"
                  value={sourceSchema}
                  onChange={(e) => setSourceSchema(e.target.value)}
                  placeholder="public"
                  className={INPUT_CLASS}
                />
              </div>
            </div>

            <ExtraFieldInputs
              fields={sourceExtraFields}
              values={sourceExtra}
              onChange={(key, value) =>
                setSourceExtra((prev) => ({ ...prev, [key]: value }))
              }
              idPrefix="pe-source-extra"
            />

            {/* ── Source credentials (always username + password) ────────── */}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <label htmlFor="pe-src-username" className={LABEL_CLASS}>
                  Username
                </label>
                <input
                  id="pe-src-username"
                  ref={sourceUsernameRef}
                  type="text"
                  autoComplete="username"
                  placeholder="e.g. ztd_reader"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="pe-src-password" className={LABEL_CLASS}>
                  Password
                </label>
                {/* SECURITY: type="password", no value/defaultValue from API data. */}
                <input
                  id="pe-src-password"
                  ref={sourcePasswordRef}
                  type="password"
                  autoComplete="new-password"
                  placeholder={
                    savedRefs.sourceSecretRef
                      ? "Enter new password to rotate"
                      : "Password"
                  }
                  className={INPUT_CLASS}
                />
              </div>
            </div>
            {savedRefs.sourceSecretRef !== undefined && (
              <SavedRefBadge
                label="Source credentials saved"
                secretRef={savedRefs.sourceSecretRef}
              />
            )}
            <button
              type="button"
              onClick={() => void handleTestSource()}
              disabled={sourceTesting || submitting}
              aria-label="Test source connection"
              className="flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sourceTesting && (
                <Loader2
                  size={12}
                  className="animate-spin"
                  aria-hidden="true"
                />
              )}
              Test Source Connection
            </button>
            {(sourceTestResult !== null || sourceTestError !== null) && (
              <div role="status" aria-live="polite">
                {sourceTestError !== null && (
                  <p className="text-red-400 text-sm" role="alert">
                    {sourceTestError}
                  </p>
                )}
                {sourceTestResult !== null && (
                  <ConnectionStatusBadge
                    label="Source"
                    ok={sourceTestResult.ok}
                    errorDetail={sourceTestResult.error}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Target connection ─────────────────────────────────────────── */}
        <div>
          <p className={SECTION_CLASS}>Target Connection</p>
          <div className="space-y-3">
            {targetConnections.length > 0 && (
              <div className="flex items-end gap-2 pb-1">
                <div className="flex-1">
                  <label htmlFor="pe-target-saved-conn" className={LABEL_CLASS}>
                    Reuse a saved connection
                  </label>
                  <select
                    id="pe-target-saved-conn"
                    value={selectedTargetConnId}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedTargetConnId(value);
                      if (value) void handleUseSavedConnection("target", value);
                    }}
                    className={INPUT_CLASS}
                  >
                    <option value="">Choose a saved connection…</option>
                    {targetConnections.map((c) => (
                      <option key={c.id} value={c.id}>
                        {connLabel(c)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => void handleUseSavedConnection("target")}
                  disabled={!selectedTargetConnId || applyingTargetConn}
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {applyingTargetConn && (
                    <Loader2
                      size={12}
                      className="animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  Use This Connection
                </button>
              </div>
            )}
            <div>
              <label htmlFor="pe-target-type" className={LABEL_CLASS}>
                Target Type
              </label>
              <select
                id="pe-target-type"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value)}
                className={INPUT_CLASS}
              >
                {connectorOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="pe-target-host" className={LABEL_CLASS}>
                {targetLabels.host}
              </label>
              <input
                id="pe-target-host"
                type="text"
                value={targetHost}
                onChange={(e) => setTargetHost(e.target.value)}
                placeholder={
                  isOAuthTarget
                    ? "adb-xxxx.azuredatabricks.net"
                    : "db.example.com"
                }
                className={INPUT_CLASS}
              />
            </div>

            {isOAuthTarget ? (
              /* Databricks: HTTP path for the SQL warehouse */
              <div>
                <label htmlFor="pe-target-http-path" className={LABEL_CLASS}>
                  SQL Warehouse Path
                </label>
                <input
                  id="pe-target-http-path"
                  type="text"
                  value={targetHttpPath}
                  onChange={(e) => setTargetHttpPath(e.target.value)}
                  placeholder="/sql/1.0/warehouses/xxxxxxxxx"
                  className={INPUT_CLASS}
                />
              </div>
            ) : (
              /* Standard RDBMS: port */
              <div>
                <label htmlFor="pe-target-port" className={LABEL_CLASS}>
                  Port
                </label>
                <input
                  id="pe-target-port"
                  type="text"
                  inputMode="numeric"
                  value={targetPort}
                  onChange={(e) => setTargetPort(e.target.value)}
                  placeholder={
                    activeTargetConnector?.default_port
                      ? String(activeTargetConnector.default_port)
                      : "5432"
                  }
                  className={INPUT_CLASS}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="pe-target-catalog" className={LABEL_CLASS}>
                  {targetLabels.catalog}
                </label>
                <input
                  id="pe-target-catalog"
                  type="text"
                  value={targetCatalog}
                  onChange={(e) => setTargetCatalog(e.target.value)}
                  placeholder={isOAuthTarget ? "main" : "my_database"}
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label htmlFor="pe-target-schema" className={LABEL_CLASS}>
                  {targetLabels.schema}
                </label>
                <input
                  id="pe-target-schema"
                  type="text"
                  value={targetSchema}
                  onChange={(e) => setTargetSchema(e.target.value)}
                  placeholder="default"
                  className={INPUT_CLASS}
                />
              </div>
            </div>

            <ExtraFieldInputs
              fields={targetExtraFields}
              values={targetExtra}
              onChange={(key, value) =>
                setTargetExtra((prev) => ({ ...prev, [key]: value }))
              }
              idPrefix="pe-target-extra"
            />

            {/* ── Target credentials — dynamic per connector type ────────── */}
            <div className="grid grid-cols-2 gap-3 pt-1">
              {isOAuthTarget ? (
                <>
                  <div>
                    <label htmlFor="pe-tgt-client-id" className={LABEL_CLASS}>
                      Client ID
                    </label>
                    <input
                      id="pe-tgt-client-id"
                      ref={targetClientIdRef}
                      type="text"
                      autoComplete="username"
                      placeholder="OAuth Client ID"
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="pe-tgt-client-secret"
                      className={LABEL_CLASS}
                    >
                      Client Secret
                    </label>
                    {/* SECURITY: type="password", never pre-populated. */}
                    <input
                      id="pe-tgt-client-secret"
                      ref={targetClientSecretRef}
                      type="password"
                      autoComplete="new-password"
                      placeholder={
                        savedRefs.targetSecretRef
                          ? "Enter new secret to rotate"
                          : "OAuth Client Secret"
                      }
                      className={INPUT_CLASS}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label htmlFor="pe-tgt-username" className={LABEL_CLASS}>
                      Username
                    </label>
                    <input
                      id="pe-tgt-username"
                      ref={targetUsernameRef}
                      type="text"
                      autoComplete="username"
                      placeholder="e.g. ztd_writer"
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label htmlFor="pe-tgt-password" className={LABEL_CLASS}>
                      Password
                    </label>
                    {/* SECURITY: type="password", never pre-populated. */}
                    <input
                      id="pe-tgt-password"
                      ref={targetPasswordRef}
                      type="password"
                      autoComplete="new-password"
                      placeholder={
                        savedRefs.targetSecretRef
                          ? "Enter new password to rotate"
                          : "Password"
                      }
                      className={INPUT_CLASS}
                    />
                  </div>
                </>
              )}
            </div>
            {savedRefs.targetSecretRef !== undefined && (
              <SavedRefBadge
                label="Target credentials saved"
                secretRef={savedRefs.targetSecretRef}
              />
            )}
            <button
              type="button"
              onClick={() => void handleTestTarget()}
              disabled={targetTesting || submitting}
              aria-label="Test target connection"
              className="flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {targetTesting && (
                <Loader2
                  size={12}
                  className="animate-spin"
                  aria-hidden="true"
                />
              )}
              Test Target Connection
            </button>
            {(targetTestResult !== null || targetTestError !== null) && (
              <div role="status" aria-live="polite">
                {targetTestError !== null && (
                  <p className="text-red-400 text-sm" role="alert">
                    {targetTestError}
                  </p>
                )}
                {targetTestResult !== null && (
                  <ConnectionStatusBadge
                    label="Target"
                    ok={targetTestResult.ok}
                    errorDetail={targetTestResult.error}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Schema access validation ─────────────────────────────────── */}
        {savedPipeline && (
          <div>
            <p className={SECTION_CLASS}>Schema Access Validation</p>
            <p className="text-xs text-white/40 mb-2">
              Confirms the saved credentials can read table/column metadata for
              the source and target — not just that a connection succeeds. Run
              this before creating a mapping sheet.
            </p>
            <SchemaAccessPanel pipelineId={savedPipeline.id} />
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────────────── */}
        {reuseError !== null && (
          <p className="text-red-400 text-sm" role="alert">
            {reuseError}
          </p>
        )}
        {error !== null && (
          <p className="text-red-400 text-sm" role="alert">
            {error}
          </p>
        )}

        {/* ── Actions ──────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && (
                <Loader2
                  size={14}
                  className="animate-spin"
                  aria-hidden="true"
                />
              )}
              Save Pipeline
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components / helpers
// ---------------------------------------------------------------------------

function stripCoreKeys(
  config: Record<string, string> | undefined,
  extraCoreKeys: string[] = [],
): Record<string, string> {
  if (!config) return {};
  const coreKeys = new Set([
    "host",
    "port",
    "database",
    "schema",
    "catalog",
    ...extraCoreKeys,
  ]);
  return Object.fromEntries(
    Object.entries(config).filter(([k]) => !coreKeys.has(k)),
  );
}

function ExtraFieldInputs({
  fields,
  values,
  onChange,
  idPrefix,
}: {
  fields: ConnectorFieldOut[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  idPrefix: string;
}) {
  if (fields.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3">
      {fields.map((field) => (
        <div key={field.key}>
          <label htmlFor={`${idPrefix}-${field.key}`} className={LABEL_CLASS}>
            {field.label}
            {field.required && (
              <span className="text-red-400" aria-hidden="true">
                {" "}
                *
              </span>
            )}
          </label>
          <input
            id={`${idPrefix}-${field.key}`}
            type="text"
            value={values[field.key] ?? ""}
            onChange={(e) => onChange(field.key, e.target.value)}
            placeholder={field.default ?? undefined}
            required={field.required}
            aria-required={field.required}
            className={INPUT_CLASS}
          />
          {field.help_text && (
            <p className="text-xs text-white/40 mt-1">{field.help_text}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function SavedRefBadge({
  label,
  secretRef,
}: {
  label: string;
  secretRef: string | undefined;
}) {
  return (
    <div className="flex items-center gap-2">
      <Check size={12} className="text-green-400 shrink-0" aria-hidden="true" />
      <span className="text-xs text-white/40">
        {label}
        {secretRef && (
          <span className="font-mono break-all"> ({secretRef})</span>
        )}
      </span>
    </div>
  );
}

function ConnectionStatusBadge({
  label,
  ok,
  errorDetail,
}: {
  label: string;
  ok: boolean;
  errorDetail?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2 text-sm">
        <span aria-hidden="true">{ok ? "✅" : "❌"}</span>
        <span className={ok ? "text-green-400" : "text-red-400"}>
          {label} {ok ? "connected" : "failed"}
        </span>
      </div>
      {!ok && errorDetail && (
        <p className="text-xs text-white/40 ml-6">{errorDetail}</p>
      )}
    </div>
  );
}
